import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaCheck, FaCopy, FaFlag, FaGamepad, FaLink, FaSignOutAlt, FaUsers } from "react-icons/fa";
import ChessBoard from "./ChessBoard";
import {
  createInitialBoard,
  createInitialGameState,
  getGameStatus,
  legalMovesForPiece,
  makeMove,
  positionKey,
} from "./chessEngine";
import { loadChessAppearance } from "./chessThemes";
import {
  createGameSession,
  getGameSession,
  getGameSessionPlayers,
  getMyOpenGameSession,
  joinGameSession,
  leaveGameSession,
  resignChessGame,
  setGamePlayerReady,
  submitChessMove,
  subscribeToGameSession,
} from "../../../services/games/gameService";
import GameRoomBackButton from "../GameRoomBackButton";
import "./Chess.css";
import "./ChessThemes.css";
import "./OnlineChess.css";

function initialSnapshot() {
  const board = createInitialBoard();
  const state = createInitialGameState();
  return {
    board,
    state,
    history: [],
    lastMove: null,
    positionCounts: { [positionKey(board, state)]: 1 },
    gameResult: { over: false, reason: "", winner: null, check: false, claimable: null },
  };
}

function normalizeSnapshot(value) {
  const fallback = initialSnapshot();
  if (!value || !Array.isArray(value.board) || !value.state) return fallback;
  return {
    ...fallback,
    ...value,
    history: Array.isArray(value.history) ? value.history : [],
    positionCounts: value.positionCounts || fallback.positionCounts,
    gameResult: value.gameResult || fallback.gameResult,
  };
}

export default function OnlineChess({ currentUser }) {
  const appearance = useMemo(() => loadChessAppearance(), []);
  const [joinCodeInput, setJoinCodeInput] = useState("");
  const [session, setSession] = useState(null);
  const [players, setPlayers] = useState([]);
  const [snapshot, setSnapshot] = useState(initialSnapshot);
  const [selected, setSelected] = useState(null);
  const [legalMoves, setLegalMoves] = useState([]);
  const [pendingPromotion, setPendingPromotion] = useState(null);
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState("");

  const myPlayer = players.find((p) => p.user_id === currentUser?.id && !p.left_at);
  const myColor = myPlayer?.seat === 1 ? "w" : myPlayer?.seat === 2 ? "b" : null;
  const opponent = players.find((p) => p.user_id !== currentUser?.id && !p.left_at);
  const myTurn = Boolean(
    session?.status === "active" &&
      session?.current_player_id === currentUser?.id &&
      snapshot.state?.turn === myColor &&
      !snapshot.gameResult?.over
  );

  const refreshTable = useCallback(async (sessionId) => {
    if (!sessionId) return;
    try {
      const [{ session: nextSession }, nextPlayers] = await Promise.all([
        getGameSession(sessionId),
        getGameSessionPlayers(sessionId),
      ]);
      setSession(nextSession);
      setPlayers(nextPlayers || []);
      setSnapshot(normalizeSnapshot(nextSession.game_state));
    } catch (err) {
      console.error("Online Chess table refresh failed:", err);
      setMessage(err.message || "Could not refresh the chess table.");
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const recoverOpenTable = async () => {
      if (!currentUser?.id || session?.id) return;
      try {
        const openSessionId = await getMyOpenGameSession("chess");
        if (!cancelled && openSessionId) await refreshTable(openSessionId);
      } catch (err) {
        console.warn("Online Chess reconnect lookup failed:", err);
      }
    };

    recoverOpenTable();
    return () => { cancelled = true; };
  }, [currentUser?.id, session?.id, refreshTable]);

  useEffect(() => {
    if (!session?.id) return undefined;

    const unsubscribe = subscribeToGameSession(session.id, {
      onSessionChange: (payload) => {
        if (payload?.new?.id === session.id) {
          setSession(payload.new);
          if (payload.new.game_state) setSnapshot(normalizeSnapshot(payload.new.game_state));
        }
      },
      onPlayersChange: () => refreshTable(session.id),
      onAction: (payload) => {
        if (payload?.new?.action_type === "chess_move" && payload.new.payload) {
          setSnapshot(normalizeSnapshot(payload.new.payload));
          setSelected(null);
          setLegalMoves([]);
          setPendingPromotion(null);
        }
        if (payload?.new?.action_type === "chess_resign") refreshTable(session.id);
      },
    });

    return unsubscribe;
  }, [session?.id, refreshTable]);

  const createTable = async () => {
    if (!currentUser?.id || working) return;
    setWorking(true);
    setMessage("");
    try {
      const created = await createGameSession({
        gameKey: "chess",
        maxPlayers: 2,
        initialState: initialSnapshot(),
      });
      await refreshTable(created.session_id);
    } catch (err) {
      setMessage(err.message || "Could not create a chess table.");
    } finally {
      setWorking(false);
    }
  };

  const joinTable = async (event) => {
    event?.preventDefault?.();
    if (!currentUser?.id || !joinCodeInput.trim() || working) return;
    setWorking(true);
    setMessage("");
    try {
      const joined = await joinGameSession(joinCodeInput);
      await refreshTable(joined.session_id);
    } catch (err) {
      setMessage(err.message || "Could not join that chess table.");
    } finally {
      setWorking(false);
    }
  };

  const toggleReady = async () => {
    if (!session?.id || !myPlayer || working) return;
    setWorking(true);
    setMessage("");
    try {
      await setGamePlayerReady(session.id, !myPlayer.ready);
      await refreshTable(session.id);
    } catch (err) {
      setMessage(err.message || "Could not update ready state.");
    } finally {
      setWorking(false);
    }
  };

  const leaveTable = async () => {
    if (!session?.id || working) return;
    if (session.status === "active") {
      setMessage("Use Resign to leave an active chess game.");
      return;
    }
    setWorking(true);
    try {
      await leaveGameSession(session.id);
      setSession(null);
      setPlayers([]);
      setSnapshot(initialSnapshot());
      setMessage("");
    } catch (err) {
      setMessage(err.message || "Could not leave the table.");
    } finally {
      setWorking(false);
    }
  };

  const performMove = async (from, move, promotionPiece = "Q") => {
    if (!myTurn || working) return;
    const result = makeMove(snapshot.board, snapshot.state, from, move, promotionPiece);
    const key = positionKey(result.board, result.state);
    const nextCount = (snapshot.positionCounts?.[key] || 0) + 1;
    const positionCounts = { ...(snapshot.positionCounts || {}), [key]: nextCount };
    const gameResult = getGameStatus(result.board, result.state, nextCount);
    const nextSnapshot = {
      board: result.board,
      state: { ...result.state, turn_before: snapshot.state.turn },
      history: [...(snapshot.history || []), result.notation],
      lastMove: { from, to: { r: move.r, c: move.c } },
      positionCounts,
      gameResult,
    };

    setWorking(true);
    setMessage("");
    try {
      await submitChessMove({
        sessionId: session.id,
        snapshot: nextSnapshot,
        expectedVersion: session.version,
      });
      setSnapshot(nextSnapshot);
      setSelected(null);
      setLegalMoves([]);
      setPendingPromotion(null);
      await refreshTable(session.id);
    } catch (err) {
      console.error("Online Chess move failed:", err);
      setMessage(
        String(err.message || "").includes("STALE_GAME_STATE")
          ? "The board changed before that move was accepted. Refreshing the table…"
          : err.message || "Move was not accepted."
      );
      await refreshTable(session.id);
    } finally {
      setWorking(false);
    }
  };

  const handleSquareClick = (r, c) => {
    if (!myTurn || working || pendingPromotion || snapshot.gameResult?.over) return;
    const piece = snapshot.board[r][c];

    if (selected) {
      const move = legalMoves.find((candidate) => candidate.r === r && candidate.c === c);
      if (move) {
        if (move.promotion) {
          setPendingPromotion({ from: selected, move });
          return;
        }
        performMove(selected, move);
        return;
      }
    }

    if (piece && piece[0] === snapshot.state.turn && piece[0] === myColor) {
      setSelected({ r, c });
      setLegalMoves(legalMovesForPiece(snapshot.board, r, c, snapshot.state));
      return;
    }

    setSelected(null);
    setLegalMoves([]);
  };

  const resign = async () => {
    if (!session?.id || session.status !== "active" || working) return;
    if (!window.confirm("Resign this online chess game?")) return;
    setWorking(true);
    try {
      await resignChessGame(session.id);
      await refreshTable(session.id);
    } catch (err) {
      setMessage(err.message || "Could not resign the game.");
    } finally {
      setWorking(false);
    }
  };

  const copyCode = async () => {
    if (!session?.join_code) return;
    try {
      await navigator.clipboard.writeText(session.join_code);
      setMessage("Invite code copied.");
    } catch {
      setMessage(`Invite code: ${session.join_code}`);
    }
  };

  if (!currentUser) {
    return (
      <div className="online-chess-page">
        <div className="online-chess-shell online-chess-login">
          <GameRoomBackButton standalone />
          <FaUsers />
          <h1>Online Chess</h1>
          <p>Sign in to Hobbli to create or join a live chess table.</p>
          <Link to="/login" className="online-chess-primary">Sign In</Link>
          <Link to="/games/chess" className="online-chess-secondary">Play Solo / Local Chess</Link>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="online-chess-page">
        <div className="online-chess-shell">
          <div className="online-chess-topbar">
            <GameRoomBackButton standalone />
            <Link to="/games/chess">Solo / Local Chess</Link>
          </div>
          <header className="online-chess-hero">
            <span>HOBBLI LIVE · BETA</span>
            <h1>Online Chess</h1>
            <p>Create a private table, share the six-character invite code, and play move-for-move through Hobbli Realtime.</p>
          </header>

          <div className="online-chess-lobby-grid">
            <section>
              <FaGamepad />
              <h2>Create a Table</h2>
              <p>You take White. Your invited opponent takes Black.</p>
              <button className="online-chess-primary" onClick={createTable} disabled={working}>
                {working ? "Creating…" : "Create Chess Table"}
              </button>
            </section>

            <section>
              <FaLink />
              <h2>Join a Table</h2>
              <p>Enter the invite code from another Hobbli player.</p>
              <form onSubmit={joinTable}>
                <input
                  value={joinCodeInput}
                  onChange={(event) => setJoinCodeInput(event.target.value.toUpperCase())}
                  maxLength={6}
                  placeholder="ABC123"
                  aria-label="Chess invite code"
                />
                <button className="online-chess-primary" disabled={working || !joinCodeInput.trim()}>
                  Join Table
                </button>
              </form>
            </section>
          </div>
          {message ? <div className="online-chess-message">{message}</div> : null}
        </div>
      </div>
    );
  }

  const white = players.find((p) => p.seat === 1 && !p.left_at);
  const black = players.find((p) => p.seat === 2 && !p.left_at);
  const waiting = session.status === "waiting";
  const currentTurnName = session.current_player_id === white?.user_id ? white?.display_name : black?.display_name;
  const statusText = waiting
    ? players.filter((p) => !p.left_at).length < 2
      ? "Waiting for an opponent"
      : "Both players must press Ready"
    : session.status === "completed"
    ? snapshot.gameResult?.reason === "conceded"
      ? "Game ended by resignation"
      : snapshot.gameResult?.reason === "checkmate"
      ? `${snapshot.gameResult?.winner === "w" ? white?.display_name || "White" : black?.display_name || "Black"} wins by checkmate`
      : "Game complete"
    : myTurn
    ? "Your move"
    : `${currentTurnName || "Opponent"} to move`;

  return (
    <div className="online-chess-page">
      <div className="online-chess-shell">
        <div className="online-chess-topbar">
          <GameRoomBackButton standalone />
          <Link to="/games/chess">Solo / Local Chess</Link>
        </div>

        <header className="online-chess-table-header">
          <div>
            <span>LIVE TABLE</span>
            <h1>Online Chess</h1>
            <p>{statusText}</p>
          </div>
          <button type="button" className="online-chess-code" onClick={copyCode} title="Copy invite code">
            <span>INVITE CODE</span><strong>{session.join_code}</strong><FaCopy />
          </button>
        </header>

        <div className="online-chess-seats">
          {[white, black].map((player, index) => (
            <div className={`online-chess-seat ${player?.user_id === currentUser.id ? "is-you" : ""}`} key={index}>
              <span>{index === 0 ? "WHITE" : "BLACK"}</span>
              <strong>{player?.display_name || "OPEN SEAT"}</strong>
              <small>{player ? (player.ready ? "READY" : waiting ? "NOT READY" : "PLAYING") : "WAITING"}</small>
            </div>
          ))}
        </div>

        {waiting ? (
          <section className="online-chess-ready-panel">
            <div>
              <h2>{players.filter((p) => !p.left_at).length < 2 ? "Share your invite code" : "Ready to play?"}</h2>
              <p>The game begins automatically as soon as both seats are filled and both players are ready.</p>
            </div>
            <div className="online-chess-ready-actions">
              <button className="online-chess-primary" onClick={toggleReady} disabled={working || !myPlayer}>
                <FaCheck /> {myPlayer?.ready ? "Not Ready" : "I'm Ready"}
              </button>
              <button className="online-chess-secondary" onClick={leaveTable} disabled={working}>
                <FaSignOutAlt /> Leave Table
              </button>
            </div>
          </section>
        ) : (
          <div className="online-chess-game-layout">
            <main className="chess-main">
              <section className={`chess-status-card ${snapshot.gameResult?.check ? "is-check" : ""}`}>
                <span className="chess-status-card__eyebrow">HOBBLI LIVE</span>
                <h2>{statusText}</h2>
                <p>You are {myColor === "w" ? "White" : "Black"}. Moves are accepted in version order and broadcast through Supabase Realtime.</p>
              </section>

              <ChessBoard
                board={snapshot.board}
                selected={selected}
                legalMoves={legalMoves}
                lastMove={snapshot.lastMove}
                inCheck={snapshot.gameResult?.check}
                turn={snapshot.state.turn}
                boardTheme={appearance.boardTheme}
                pieceSet={appearance.pieceSet}
                orientation={myColor || "w"}
                showCoordinates={appearance.showCoordinates}
                disabled={!myTurn || working || session.status !== "active"}
                onSquareClick={handleSquareClick}
              />

              <div className="chess-board-footer">
                <span>{myTurn ? "Your move" : "Waiting for opponent"}</span>
                <span>Table v{session.version}</span>
              </div>
            </main>

            <aside className="online-chess-side-panel">
              <section>
                <span>MATCH</span>
                <h3>{white?.display_name || "White"} vs {black?.display_name || "Black"}</h3>
                <p>{snapshot.history?.length || 0} half-moves played</p>
              </section>
              <section>
                <span>MOVE HISTORY</span>
                <div className="online-chess-history">
                  {(snapshot.history || []).length ? snapshot.history.map((move, index) => (
                    <b key={`${index}-${move}`}>{index + 1}. {move}</b>
                  )) : <p>No moves yet.</p>}
                </div>
              </section>
              {session.status === "active" ? (
                <button className="online-chess-resign" onClick={resign} disabled={working}>
                  <FaFlag /> Resign Game
                </button>
              ) : (
                <Link to="/games" className="online-chess-primary">Back to Game Room</Link>
              )}
            </aside>
          </div>
        )}

        {message ? <div className="online-chess-message">{message}</div> : null}
      </div>

      {pendingPromotion && (
        <div className="chess-promotion-overlay" role="dialog" aria-modal="true" aria-label="Choose promotion piece">
          <div className="chess-promotion-dialog">
            <span className="chess-side-eyebrow">Pawn promotion</span>
            <h3>Choose a piece</h3>
            <div className="chess-promotion-options">
              {[["Q", "Queen"], ["R", "Rook"], ["B", "Bishop"], ["N", "Knight"]].map(([type, label]) => (
                <button key={type} type="button" onClick={() => performMove(pendingPromotion.from, pendingPromotion.move, type)}>
                  <span>{myColor === "w" ? { Q: "♕", R: "♖", B: "♗", N: "♘" }[type] : { Q: "♛", R: "♜", B: "♝", N: "♞" }[type]}</span>
                  <strong>{label}</strong>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
